import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { SearchCriteria, SearchResult } from '@snelstart/domain-logic';
import { SharedUiTableModule, TableConfiguration, ZeroStateConfiguration } from '@snelstart/ui-table';
import {
    AllowanceEntity,
    BokkzAlgDataService,
    BokkzClient,
    BokkzTslDataService,
    ClientEntity,
    FeatureServiceComponent,
    MenuInterface,
    ToeslagenBerekening,
    TslRekenService,
} from '@snelstart-ventures/fiscaal/domain-logic';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'lib-snelstart-venture-fiscaal-feature-tax-allowance-list',
    templateUrl: './feature-tax-allowance-list.component.html',
    styleUrls: ['./feature-tax-allowance-list.component.scss'],
    imports: [CommonModule, SharedUiTableModule, SharedUiButtonModule, FeatureServiceComponent],
})
export class FeatureTaxAllowanceListComponent implements OnInit {
    @Output() public details = new EventEmitter<string>();
    protected id: any;
    protected tableConfig!: TableConfiguration<any, any>;
    private lastTableStateKey: string | null = null;
    private readonly reload$ = new BehaviorSubject<void>(undefined);

    private readonly options$ = new BehaviorSubject<MenuInterface[]>([]);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly dataService = inject(BokkzTslDataService);
    private readonly algDataService = inject(BokkzAlgDataService);
    private readonly bokkzClient = inject(BokkzClient);
    private readonly rekenService = inject(TslRekenService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private dispatchMenuTimer: ReturnType<typeof setTimeout> | null = null;

    protected clientId: any;
    public dialogVisible = false;
    public showTable = false;
    public year: any;
    public options = [] as MenuInterface[];
    public tslBerekening = <ToeslagenBerekening>{};
    public hasPartner = false;
    public sumToeslagen = 0;
    public sumVoorschot = 0;
    public rechtOpZorg = 0;
    public rechtOpKgb = 0;
    public rechtOpKot = 0;
    public rechtOpHuur = 0;
    public sumSaldo = 0;
    public activePage = '';

    readonly clientId$ = this.activatedRoute.params.pipe(
        map((params) => params['clientId'] ?? ''),
        filter((clientId) => !!clientId),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly year$ = this.algDataService.selectedYear.pipe(
        filter((year): year is number => year != null),
        distinctUntilChanged(),
        tap((year) => {
            this.year = year;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly allowanceLoad$ = combineLatest({
        clientId: this.clientId$,
        year: this.year$,
        reload: this.reload$,
    }).pipe(
        switchMap(({ clientId, year }) =>
            this.bokkzClient.getAllowance(clientId, String(year)).pipe(
                tap((allowanceEntity) => {
                    this.clientId = clientId;
                    if (allowanceEntity.toeslagen) {
                        if (allowanceEntity.toeslagen.geboortedatumAanvrager == undefined) {
                            this.bokkzClient
                                .getIncomeTax(allowanceEntity.sstRelationId!, String(allowanceEntity.toeslagen?.berekeningsjaar || 0))
                                .pipe(takeUntilDestroyed(this.destroyRef))
                                .subscribe((incomeTax) => {
                                    if (incomeTax.aangifte !== undefined) {
                                        this.rekenService
                                            .getToeslagen(incomeTax.aangifte!)
                                            .pipe(takeUntilDestroyed(this.destroyRef))
                                            .subscribe((toeslagen) => {
                                                this.dataService.patchAllowanceEntity((entity) => ({
                                                    ...entity,
                                                    toeslagen: toeslagen,
                                                }));
                                            });
                                        this.dataService.incomeTaxEntity.next(incomeTax);
                                    }
                                });
                        }
                        this.dataService.updateAllowanceTaxEntity(allowanceEntity);
                        this.dataService.calculateLatestAllowances().subscribe();
                    }
                }),
                catchError(() => of(null)),
            ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly clientEntity$: Observable<ClientEntity> = this.algDataService.clientEntity$.pipe(
        filter((data): data is ClientEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly tableVm$ = this.allowanceLoad$.pipe(
        switchMap((allowanceEntity) => {
            if (!allowanceEntity?.toeslagen) {
                this.resetSums();
                this.dataService.updateAantalValidaties(0);
                this.dataService.menuInterface.next([]);
                this.options$.next([]);

                return of({
                    allowanceEntity: null,
                    options: [] as MenuInterface[],
                    tableConfig: this.createTableConfig([]),
                });
            }

            this.dataService.createMenuInterface();
            const options = this.dataService.menuInterface.value ?? [];
            this.options$.next([...options]);

            return of({
                allowanceEntity,
                options,
                tableConfig: this.createTableConfig(options),
            });
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly allowanceEntity$: Observable<AllowanceEntity> = this.dataService.allowanceEntity.pipe(
        filter((data): data is AllowanceEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly tslBerekening$: Observable<ToeslagenBerekening> = this.dataService.tslBerekening$.pipe(
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public onAllowanceCreated(): void {
        this.dataService.createMenuInterface();

        const options = this.dataService.menuInterface.value ?? [];
        this.options$.next([...options]);
        this.options = options;

        this.tableConfig = this.createTableConfig(options);
        this.cdr.detectChanges();
        this.showTable = true;
        this.reload$.next();
    }

    private resetSums(): void {
        this.sumToeslagen = 0;
        this.sumVoorschot = 0;
        this.rechtOpZorg = 0;
        this.rechtOpKgb = 0;
        this.rechtOpKot = 0;
        this.rechtOpHuur = 0;
        this.sumSaldo = 0;
    }

    ngOnInit(): void {
        this.tslBerekening$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.tslBerekening = value;
            this.getSumToeslagen();
            this.rechtOpHuur = value.rechtOpHuurtoeslag || 0;
            this.rechtOpZorg = value.rechtOpZorgtoeslag || 0;
            this.rechtOpKgb = value.rechtOpKGB || 0;
            this.rechtOpKot = value.rechtOpKOT || 0;
        });

        this.dataService.allowanceEntity
            .pipe(
                debounceTime(0),
                filter((allowanceEntity): allowanceEntity is AllowanceEntity => !!allowanceEntity?.toeslagen),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.dataService.createMenuInterface();
            });

        this.tableVm$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vm) => {
            this.options = vm.options;

            const tableStateKey = vm.allowanceEntity?.id != null ? `data-${vm.allowanceEntity.id}-${this.year}` : `empty-${this.year}`;

            if (this.lastTableStateKey !== tableStateKey) {
                this.lastTableStateKey = tableStateKey;

                this.showTable = false;
                this.tableConfig = undefined as any;
                this.cdr.detectChanges();

                this.tableConfig = vm.tableConfig;
                this.showTable = true;
                this.cdr.detectChanges();
            } else {
                this.tableConfig = vm.tableConfig;
                this.showTable = true;
            }
        });

        this.dataService.menuInterface.pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef)).subscribe((menuInterface) => {
            const old = this.options$.value;
            const next = [...(menuInterface ?? [])];

            if (JSON.stringify(old) === JSON.stringify(next)) {
                return;
            }

            this.options$.next(next);
            this.options = next;
            this.tableConfig = this.createTableConfig(next);
            this.cdr.detectChanges();
        });
    }

    private createTableConfig(options: MenuInterface[]): TableConfiguration<any, any> {
        const criteria = new SearchCriteria();

        return TableConfiguration.create({
            identifier: 'value',
            options: {
                useSearch: false,
                useSort: true,
            },
            criteria,

            search: () => {
                const sortedGroups = this.sortVisibleMenu(options);
                return of(new SearchResult(sortedGroups, sortedGroups.length));
            },

            details: (entity) => {
                if (entity) {
                    this.details.next(entity.label);
                }
            },

            fromDetailsMap: (entity) => {
                return entity as unknown as Record<number, MenuInterface>;
            },

            zeroStateConfig: ZeroStateConfiguration.create({
                icon: 'book-open-cover',
                mainText: this.year < 2024 ? 'Module niet beschikbaar' : $localize`Geen toeslagen gevonden voor ` + this.year,
                subText:
                    this.year < 2024
                        ? 'De module toeslagen wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
                        : 'Maak een nieuwe toeslagenberekening aan en start direct met invullen.',
                buttonText: this.year < 2024 ? undefined : `Maak nieuwe toeslagenberekening`,
                buttonConfig:
                    this.year < 2024
                        ? undefined
                        : ButtonConfiguration.create({
                              onClick: () =>
                                  new Observable((subscriber) => {
                                      this.dialogVisible = true;
                                      subscriber.next(true);
                                      subscriber.complete();
                                  }),
                          }),
            }),
        });
    }

    private sortVisibleMenu(options: MenuInterface[]): MenuInterface[] {
        return [...options]
            .filter((item) => item.visible)
            .map((group) => ({
                ...group,
                subonderdelen: (group.subonderdelen || [])
                    .filter((sub) => sub.visible)
                    .sort((a, b) => a.value - b.value)
                    .map((sub) => ({ ...sub })),
            }))
            .sort((a, b) => a.value - b.value);
    }

    public navigateToSubPage(pageMapping: string): void {
        const url = '/tax-allowance/' + 'modules/' + this.clientId + '/' + pageMapping;
        this.router.navigate([url]).then(() => console.log('complete'));
        this.activePage = pageMapping;
    }

    public getSumToeslagen() {
        const kot1 = this.tslBerekening?.verschuldigdeKOT || 0;
        const kgb1 = this.tslBerekening?.verschuldigdeKGB || 0;
        const huur1 = this.tslBerekening?.verschuldigdeHuurtoeslag || 0;
        const zorg1 = this.tslBerekening?.verschuldigdeZorgtoeslag || 0;
        this.sumSaldo = kot1 + kgb1 + huur1 + zorg1;

        const kot2 = this.tslBerekening?.alOntvangenKOT || 0;
        const kgb2 = this.tslBerekening?.alOntvangenKGB || 0;
        const huur2 = this.tslBerekening?.alOntvangenHuurtoeslag || 0;
        const zorg2 = this.tslBerekening?.alOntvangenZorgtoeslag || 0;
        this.sumVoorschot = kot2 + kgb2 + huur2 + zorg2;

        const kot3 = this.tslBerekening?.rechtOpKOT || 0;
        const kgb3 = this.tslBerekening?.rechtOpKGB || 0;
        const huur3 = this.tslBerekening?.rechtOpHuurtoeslag || 0;
        const zorg3 = this.tslBerekening?.rechtOpZorgtoeslag || 0;
        this.sumToeslagen = kot3 + kgb3 + huur3 + zorg3;
    }
}
